import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Field, Form } from 'formik';
import AprosButton from 'components/aprosButton/index';
import Path from 'constants/routePath';
import Proctitle from 'components/proctitle/index';
import InputTextGuest from 'components/inputTextGuest/index';
import InputTitleLabelGuest from 'components/inputTitleLabelGuest/index';
import axios from 'utils/axiosUtil';
import dayjs from 'dayjs';
import { GUEST_RESERVE_INFO } from 'constants/apiURL';
import { accountActions } from 'slices/accountSlice';
import { verifySchema } from 'utils/guestReserveValidationSchemaUtil';
import { useDeviceSize } from 'hooks/useDeviceSize';
import { getOneTimeToken } from 'utils/axiosUtil';
import Title from '../../constants/title';
import { Helmet } from 'react-helmet';

const GuestSearch = () => {
  const navigate = useNavigate();
  const { isPc } = useDeviceSize();
  const [loading, setLoading] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState('');
  const dispatch = useDispatch();
  const token = useSelector(state => state.account.token);
  const maintenanceInfo = useSelector(state => state.maintenance.maintenanceInfo);

  // 必須項目が全て入力されているか判定
  const isAllRequiredFilled = values => {
    return values.email.trim() && values.reserveNumber.trim();
  };

  // ゲスト予約情報取得APIを呼び出す
  const fetchReservationInfo = async (mailAdr, yykNo) => {
    const response = await axios.post(`${GUEST_RESERVE_INFO}`, {
      yykNo: yykNo,
      mailAdr: mailAdr,
    });
    if (response.body?.guestReservationInfo) {
      return response.body;
    } else {
      return null;
    }
  };

  // 確認画面へ進むボタン押下時の処理
  const handleSubmitWithToken = async values => {
    setErrorMsg('');
    setLoading(true);
    // トークンがなければ取得
    if (!token) {
      const oneTimeToken = await getOneTimeToken();
      dispatch(accountActions.updateToken({ token: oneTimeToken }));
    }
    const reservationInfo = await fetchReservationInfo(
      values.email,
      values.reserveNumber
    );
    if (reservationInfo && reservationInfo.guestReservationInfo?.yykNo) {
      navigate(Path.GUEST_INFO, {
        state: {
          ...reservationInfo,
          email: values.email,
          reserveNumber: values.reserveNumber,
        },
      });
    } else {
      setErrorMsg('予約情報がありません');
    }
    setLoading(false);
  };

  return (
    <div className="mt-4 min-w-[200px] lg:px-20 lg:min-w-[300px]">
      <Helmet>
        <title>{Title.GUEST_RESERVATION}</title>
      </Helmet>
      {/* メンテナンスエリア */}
      {maintenanceInfo?.display && (
        <div className="flex flex-col justify-between gap-y-1.5 border border-solid border-line bg-white px-2 py-3 sm:gap-y-4 sm:px-6 sm:py-4 lg:mt-3 lg:gap-y-6">
          <p className="text-sm font-medium text-navy">
            平素はオートバックスの各種サービスをご愛顧頂き、誠にありがとうございます。
            <br />
            下記日程にてサイトのメンテナンスを行います。
            <br />
            皆様には、ご不便をお掛けいたしますが、時間を改めて、アクセス頂きます様、宜しくお願い申し上げます。
          </p>
          <p className="whitespace-pre-wrap text-[14px] font-bold text-navy sm:text-[18px] lg:text-[20px]">
            メンテナンス予定日時{isPc ? '：' : '\n'}
            {dayjs(maintenanceInfo?.maintenanceStartDate).format('M月D日 H:mm')}
            ～{dayjs(maintenanceInfo?.maintenanceEndDate).format('M月D日 H:mm')}{' '}
            予定
          </p>
        </div>
      )}
      <div className="mt-12 sm:hidden">
        <Proctitle
          useH1={true}
          text="予約の確認・キャンセル"
          className={{ textClass: 'whitespace-nowrap' }}
        />
      </div>
      <div className="mt-12 hidden text-center sm:block">
        <h1 className="text-lg whitespace-nowrap font-bold">
          予約の確認・キャンセル
        </h1>
      </div>
      <div className="mt-8 justify-center sm:mt-6 sm:border sm:border-line sm:bg-white max-w-[800px] w-full mx-auto">
        <div className="mt-10 sm:mt-14 sm:justify-center sm:px-10 sm:text-center">
          <p>
            予約完了メール・予約完了画面に記載された予約番号と、
            <span className="hidden sm:inline">
              <br />
            </span>
            予約時に使用したメールアドレスを入力してください。
          </p>
        </div>
        <Formik
          initialValues={{
            email: '',
            reserveNumber: '',
          }}
          validateOnChange={true}
          validateOnBlur={true}
          validationSchema={verifySchema}
          onSubmit={handleSubmitWithToken}
        >
          {({
            handleSubmit,
            isValid,
            values,
          }) => (
            <Form>
              <div className="mb-6 mt-6 sm:mt-12 sm:mx-auto sm:w-full sm:max-w-[500px]">
                <div className="flex flex-col sm:flex-row sm:items-center">
                  <div className="flex min-w-[180px] flex-row sm:w-1/2 sm:min-w-[200px] sm:max-w-[210px]">
                    <InputTitleLabelGuest title="予約番号" required={true} />
                    <span className="text-xs mt-1 text-[#4C657C]">半角数字</span>
                  </div>
                  <div className="flex flex-row w-full sm:-mt-10 sm:w-[300px]">
                    <Field name="reserveNumber">
                      {({ field, form }) => (
                        <InputTextGuest
                          formik={form}
                          fieldName={field.name}
                          value={field.value}
                          guestFlag={true}
                          onChange={e => {
                            form.setFieldValue(field.name, e.target.value);
                            form.setFieldTouched(field.name, true, false);
                          }}
                          onBlur={field.onBlur}
                          placeholder="12345678"
                          errorMessage={
                            form.errors[field.name] && form.touched[field.name]
                              ? <span className="no-wrap-error">{form.errors[field.name]}</span>
                              : ''
                          }
                          touched={form.touched[field.name]}
                          className={{ boxClass: "w-full", inputClass: "w-full" }}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </div>
              <div className="mb-6 mt-6 sm:mt-12 sm:mb-12 sm:mx-auto sm:w-full sm:max-w-[500px]">
                <div className="flex flex-col sm:flex-row">
                  <div className="flex min-w-[180px] flex-row sm:w-1/2 sm:max-w-[210px]">
                    <InputTitleLabelGuest title="メールアドレス" required={true} />
                  </div>
                  <div className="flex flex-row w-full sm:-mt-10 sm:w-[300px]">
                    <Field name="email">
                      {({ field, form }) => (
                        <InputTextGuest
                          formik={form}
                          fieldName={field.name}
                          value={field.value}
                          guestFlag={true}
                          onChange={e => {
                            form.setFieldValue(field.name, e.target.value);
                            form.setFieldTouched(field.name, true, false);
                          }}
                          onBlur={field.onBlur}
                          placeholder="info@autobacs.com"
                          errorMessage={
                            form.errors[field.name] && form.touched[field.name]
                              ? form.errors[field.name]
                              : ''
                          }
                          touched={form.touched[field.name]}
                        />
                      )}
                    </Field>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                {errorMsg && (
                  <div className="text-xs mt-2 text-red">{errorMsg}</div>
                )}
              </div>
              <div className="flex flex-col items-center">
                <AprosButton
                  text="確認画面へ進む"
                  comClassName="main"
                  onClick={
                    isValid && isAllRequiredFilled(values) && !loading
                      ? handleSubmit
                      : undefined
                  }
                  className="mb-4 sm:mb-14 w-[260px]"
                  disabled={!isValid || !isAllRequiredFilled(values) || loading}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <p className="mb-10 mt-10 sm:justify-center sm:px-10 sm:text-center">
        予約番号がわからない場合や、次の画面に進めない場合は、
        <span className="hidden sm:inline">
          <br />
        </span>
        ご予約の店舗へ直接ご連絡ください。
      </p>
    </div>
  );
};

export default GuestSearch;
